import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { messagesAPI } from '../utils/api';
import Sidebar from './Sidebar';
import ChatWindow from './ChatWindow';
import ProfileModal from './ProfileModal';
import toast from 'react-hot-toast';

export default function ChatApp() {
  const { user } = useAuth();
  const { on, off, isConnected } = useSocket();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  // Fix 1: separate state to open profile modal on specific tab
  const [showProfile, setShowProfile] = useState(false);
  const [profileTab, setProfileTab] = useState('profile');
  const [loadingConvs, setLoadingConvs] = useState(true);

  const loadConversations = useCallback(async () => {
    try {
      const res = await messagesAPI.getConversations();
      setConversations(res.data.data);
    } catch (err) { console.error('Failed to load conversations:', err); }
    finally { setLoadingConvs(false); }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    const handleIncoming = (message) => {
      setConversations(prev => prev.map(conv => {
        if (conv._id === message.conversationId) {
          const isActive = activeConversation?._id === message.conversationId;
          return { ...conv, lastMessageContent: message.content, lastMessageAt: message.createdAt, unreadCount: isActive ? 0 : (conv.unreadCount || 0) + 1 };
        }
        return conv;
      }));
      if (activeConversation?._id !== message.conversationId) {
        const sender = message.sender?.name || 'Someone';
        toast(`💬 ${sender}: ${message.content?.substring(0, 40)}${message.content?.length > 40 ? '…' : ''}`, { duration: 3000 });
      }
    };
    on('message:received', handleIncoming);
    return () => off('message:received', handleIncoming);
  }, [on, off, activeConversation]);

  const openConversation = useCallback(async (targetUserId) => {
    try {
      const res = await messagesAPI.getOrCreateConversation(targetUserId);
      const { conversation, targetUser } = res.data.data;
      const shaped = { _id: conversation._id, isGroup: false, otherUser: targetUser, participant: targetUser, participants: [user, targetUser] };
      setActiveConversation(shaped);
      setConversations(prev => {
        const exists = prev.find(c => c._id === conversation._id);
        if (!exists) return [{ _id: conversation._id, isGroup: false, participant: targetUser, lastMessageContent: '', lastMessageAt: null, unreadCount: 0 }, ...prev];
        return prev.map(c => c._id === conversation._id ? { ...c, unreadCount: 0 } : c);
      });
    } catch { toast.error('Could not open conversation.'); }
  }, [user]);

  const handleSelectConversation = useCallback((conv) => {
    if (conv.isGroup) {
      setActiveConversation({ _id: conv._id, isGroup: true, groupName: conv.groupName, groupAdmin: conv.groupAdmin, participants: conv.participants });
    } else {
      setActiveConversation({ _id: conv._id, isGroup: false, otherUser: conv.participant, participant: conv.participant, participants: [user, conv.participant] });
    }
    setConversations(prev => prev.map(c => c._id === conv._id ? { ...c, unreadCount: 0 } : c));
  }, [user]);

  const handleGroupCreated = useCallback((group) => {
    const newConv = { _id: group._id, isGroup: true, groupName: group.groupName, groupAdmin: group.groupAdmin, participants: group.participants || [], lastMessageContent: '', lastMessageAt: null, unreadCount: 0 };
    setConversations(prev => [newConv, ...prev]);
    setActiveConversation({ _id: group._id, isGroup: true, groupName: group.groupName, groupAdmin: group.groupAdmin, participants: group.participants || [] });
  }, []);

  // Fix 1: open profile on "password" tab directly
  const openChangePassword = useCallback(() => {
    setProfileTab('password');
    setShowProfile(true);
  }, []);

  const openProfile = useCallback(() => {
    setProfileTab('profile');
    setShowProfile(true);
  }, []);

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  return (
    <div className="chat-layout">
      <Sidebar
        conversations={conversations}
        loadingConvs={loadingConvs}
        activeConversationId={activeConversation?._id}
        onSelectConversation={handleSelectConversation}
        onOpenNewChat={openConversation}
        onOpenProfile={openProfile}
        onOpenChangePassword={openChangePassword}
        totalUnread={totalUnread}
        isConnected={isConnected}
        onGroupCreated={handleGroupCreated}
      />
      <div className="chat-main">
        {activeConversation ? (
          <ChatWindow key={activeConversation._id} conversation={activeConversation} currentUser={user} />
        ) : (
          <div className="chat-empty">
            <div className="chat-empty-icon">💬</div>
            <h2>Welcome to TeamChat</h2>
            <p>Select a conversation from the sidebar, or click the People tab to start a new chat with a colleague.</p>
            {!isConnected && <div className="alert alert-warn" style={{ marginTop: 8, fontSize: 13 }}>⚠ Connecting to server…</div>}
          </div>
        )}
      </div>
      {showProfile && (
        <ProfileModal
          initialTab={profileTab}
          onClose={() => setShowProfile(false)}
        />
      )}
    </div>
  );
}
